// src/components/ScoreboardTabs.js (actualizado)
import React, { useState, useEffect } from 'react';
import { Tabs, Tab, Box } from '@mui/material';
import TabPanelScoreboard from './TabPanelScoreboard';
import TableroPrincipal from './TableroPrincipal';
import GestionTableros from './GestionTableros';

const ScoreboardTabs = () => {
  const [activeTab, setActiveTab] = useState(0);

  // Estados para manejar las configuraciones y ventanas abiertas
  const [configurations, setConfigurations] = useState(() => {
    const savedConfigs = localStorage.getItem('configurations');
    return savedConfigs ? JSON.parse(savedConfigs) : [];
  });
  const [openWindows, setOpenWindows] = useState({});

  // Función para guardar configuraciones en localStorage y actualizar estado
  const updateConfigurations = (newConfigs) => {
    setConfigurations(newConfigs);
    localStorage.setItem('configurations', JSON.stringify(newConfigs));
  };

  // Función para actualizar una configuración específica
  const updateConfiguration = (configId, updatedConfig) => {
    // Si updatedConfig es null, eliminar la configuración
    if (updatedConfig === null) {
      const newConfigs = configurations.filter(config => config.id !== configId);
      updateConfigurations(newConfigs);
      return;
    }

    const newConfigs = configurations.map(config => 
      config.id === configId ? { ...config, ...updatedConfig } : config
    );
    updateConfigurations(newConfigs);
    
    // También actualizamos la configuración específica en localStorage
    const updatedConfigObj = newConfigs.find(config => config.id === configId);
    if (updatedConfigObj) {
      localStorage.setItem(`config-${configId}`, JSON.stringify(updatedConfigObj));
      // Actualizamos tableros abiertos
      refreshTablero(configId);
    }
  };

  // Función para actualizar un campo específico de una configuración
  const updateConfigField = (configId, field, value) => {
    const configToUpdate = configurations.find(config => config.id === configId);
    if (!configToUpdate) return false;
    
    const updatedConfig = { ...configToUpdate };
    
    if (field === 'name' || field === 'boardType') {
      updatedConfig[field] = value;
    } else {
      // Aseguramos que settings exista
      if (!updatedConfig.settings) updatedConfig.settings = {};
      
      // Actualizamos el campo en settings
      updatedConfig.settings = {
        ...updatedConfig.settings,
        [field]: value
      };
    }
    
    // Actualizar en el array de configuraciones
    const newConfigs = configurations.map(config => 
      config.id === configId ? updatedConfig : config
    );
    
    updateConfigurations(newConfigs);
    
    // También actualizamos la configuración específica en localStorage
    localStorage.setItem(`config-${configId}`, JSON.stringify(updatedConfig));
    
    // YA NO actualizamos tableros abiertos automáticamente
    // refreshTablero(configId);
    
    return true;
  };

  // Función para abrir un tablero
  const openTablero = (configId) => {
    const configToLoad = configurations.find((config) => config.id === configId);
    if (configToLoad) {
      // Guardar en localStorage
      localStorage.setItem(`config-${configId}`, JSON.stringify(configToLoad));
      
      // Obtener el tipo de tablero desde la configuración
      const boardType = configToLoad.boardType || 'track';
      
      // Preparar dimensiones
      const width = configToLoad.settings?.tableWidth || 800;
      const height = configToLoad.settings?.tableHeight || 600;
      
      // Crear un identificador único para esta instancia de ventana
      const uniqueWindowId = `tablero-${configId}-${Date.now()}`;
      
      // Incluir el tipo en la URL
      const newWindow = window.open(
        `/scoreboard?id=${configId}&type=${boardType}`, 
        uniqueWindowId,
        `width=${width + 40},height=${height + 40},menubar=no,toolbar=no,location=no,status=no`
      );
      
      if (newWindow) {
        // Guardar referencia
        setOpenWindows((prevWindows) => ({ 
          ...prevWindows, 
          [uniqueWindowId]: { window: newWindow, configId }
        }));
        
        // Limpiar cuando se cierre
        newWindow.addEventListener('beforeunload', () => {
          setOpenWindows((prevWindows) => {
            const updatedWindows = { ...prevWindows };
            delete updatedWindows[uniqueWindowId];
            return updatedWindows;
          });
        });
      } else {
        alert('No se pudo abrir la ventana. Por favor, permite las ventanas emergentes en tu navegador.');
      }
    }
  };

  // Función para actualizar todos los tableros abiertos de una configuración
  const refreshTablero = (configId) => {
    let foundWindows = false;
    
    Object.entries(openWindows).forEach(([windowId, windowData]) => {
      if (windowData.configId === configId && !windowData.window.closed) {
        windowData.window.postMessage({ 
          type: 'updateConfig', 
          configId,
          timestamp: Date.now() // Para evitar caché
        }, '*');
        foundWindows = true;
      }
    });
    
    return foundWindows;
  };

  // Limpiar ventanas cerradas periódicamente
  useEffect(() => {
    const cleanupInterval = setInterval(() => {
      setOpenWindows(prevWindows => {
        const updatedWindows = {};
        let changed = false;
        
        Object.entries(prevWindows).forEach(([id, windowData]) => {
          if (!windowData.window.closed) {
            updatedWindows[id] = windowData;
          } else {
            changed = true;
          }
        });
        
        return changed ? updatedWindows : prevWindows;
      });
    }, 30000); // Cada 30 segundos
    
    return () => clearInterval(cleanupInterval);
  }, []);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  return (
    <Box sx={{ width: '100%' }}>
      <Tabs
        value={activeTab}
        onChange={handleTabChange}
        centered
        variant="fullWidth"
        indicatorColor="secondary"
        textColor="inherit"
      >
        <Tab label="Ajustes" />
        <Tab label="Tableros" />
      </Tabs>

      <TabPanelScoreboard value={activeTab} index={0}>
        <TableroPrincipal
          configurations={configurations}
          setConfigurations={updateConfigurations}
          updateConfiguration={updateConfiguration}
          updateConfigField={updateConfigField}
          openTablero={openTablero}
          refreshTablero={refreshTablero}
          openWindows={openWindows}
          setOpenWindows={setOpenWindows}
        />
      </TabPanelScoreboard>

      <TabPanelScoreboard value={activeTab} index={1}>
        <GestionTableros
          configurations={configurations}
          updateConfiguration={updateConfiguration}
          updateConfigField={updateConfigField}
          openTablero={openTablero}
          refreshTablero={refreshTablero}
          openWindows={openWindows}
        />
      </TabPanelScoreboard>
    </Box>
  );
};

export default ScoreboardTabs;